import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  IconButton,
  Input,
  Modal,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTable,
} from '@/components/ui'
import type { BwaPosition, PlanAdjustment, AdjustmentType } from './types.ts'

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

export function AdjustmentsTab({
  planId, year, positions, adjustments, onChange,
}: {
  planId: number
  year: number
  positions: BwaPosition[]
  adjustments: PlanAdjustment[]
  onChange: () => void
}) {
  const [createOpen, setCreateOpen] = useState(false)

  async function handleDelete(id: number) {
    if (!confirm('Anpassung wirklich löschen?')) return
    await apiClient.delete(`/liquidity-planning/plans/${planId}/adjustments/${id}`)
    onChange()
  }

  function fmtPeriod(a: PlanAdjustment): string {
    const start = `${MONTH_LABELS[a.startMonth - 1]} ${a.startYear}`
    if (a.type === 'einmalig') return start
    const end = a.endYear !== null && a.endMonth !== null
      ? `${MONTH_LABELS[a.endMonth - 1]} ${a.endYear}`
      : '∞'
    return `${start} – ${end}`
  }

  const positionLabel = (code: string) => positions.find((p) => p.code === code)?.label ?? code

  return (
    <Card>
      <Card.Header>
        <div className="flex justify-between items-center">
          <span className="text-h5">Einzelpositionen / Anpassungen</span>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" /> Neue Anpassung
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <SimpleTable>
          <SimpleTable.Head>
            <SimpleTable.Tr>
              <SimpleTable.Th>Bezeichnung</SimpleTable.Th>
              <SimpleTable.Th>BWA-Position</SimpleTable.Th>
              <SimpleTable.Th>Zeitraum</SimpleTable.Th>
              <SimpleTable.Th>Typ</SimpleTable.Th>
              <SimpleTable.Th className="text-right">Betrag/Monat</SimpleTable.Th>
              <SimpleTable.Th></SimpleTable.Th>
            </SimpleTable.Tr>
          </SimpleTable.Head>
          <SimpleTable.Body>
            {adjustments.length === 0 && (
              <SimpleTable.Tr>
                <SimpleTable.Td colSpan={6} className="text-center text-small text-gray-500 py-6">
                  Noch keine Anpassungen — z.B. „Mitarbeiter Mustermann ab 10/2026" anlegen.
                </SimpleTable.Td>
              </SimpleTable.Tr>
            )}
            {adjustments.map((a) => (
              <SimpleTable.Tr key={a.id}>
                <SimpleTable.Td>{a.label}</SimpleTable.Td>
                <SimpleTable.Td>{positionLabel(a.positionCode)}</SimpleTable.Td>
                <SimpleTable.Td>{fmtPeriod(a)}</SimpleTable.Td>
                <SimpleTable.Td className="capitalize text-small text-gray-600">{a.type}</SimpleTable.Td>
                <SimpleTable.Td className="text-right">{EUR.format(a.amountMonthly)}</SimpleTable.Td>
                <SimpleTable.Td className="text-right">
                  <IconButton
                    icon={<Trash2 className="size-4" />}
                    aria-label="Löschen"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(a.id)}
                  />
                </SimpleTable.Td>
              </SimpleTable.Tr>
            ))}
          </SimpleTable.Body>
        </SimpleTable>
      </Card.Body>

      <CreateAdjustmentModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        planId={planId}
        defaultYear={year}
        positions={positions}
        onCreated={() => { setCreateOpen(false); onChange() }}
      />
    </Card>
  )
}

function CreateAdjustmentModal({
  open, onOpenChange, planId, defaultYear, positions, onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  planId: number
  defaultYear: number
  positions: BwaPosition[]
  onCreated: () => void
}) {
  const [positionCode, setPositionCode] = useState(positions.find((p) => p.code === 'personalkosten')?.code ?? '')
  const [label, setLabel] = useState('')
  const [type, setType] = useState<AdjustmentType>('wiederkehrend')
  const [startYear, setStartYear] = useState(defaultYear)
  const [startMonth, setStartMonth] = useState(1)
  const [endYear, setEndYear] = useState<number | null>(null)
  const [endMonth, setEndMonth] = useState<number | null>(null)
  const [amount, setAmount] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post(`/liquidity-planning/plans/${planId}/adjustments`, {
        positionCode,
        label,
        startYear,
        startMonth,
        endYear: type === 'einmalig' ? null : endYear,
        endMonth: type === 'einmalig' ? null : endMonth,
        amountMonthly: amount,
        type,
      })
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Anlegen')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Neue Einzelposition"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting || !label || !positionCode}>
            {submitting ? 'Speichere…' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label="Bezeichnung"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="z.B. Mitarbeiter Mustermann"
        />
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">BWA-Position</label>
          <SelectRoot value={positionCode} onValueChange={setPositionCode}>
            <SelectTrigger>
              <SelectValue placeholder="Position wählen" />
            </SelectTrigger>
            <SelectContent>
              {positions
                .filter((p) => !p.isAggregate && p.section !== 'liqui')
                .map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                ))}
            </SelectContent>
          </SelectRoot>
        </div>
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Typ</label>
          <SelectRoot value={type} onValueChange={(v) => setType(v as AdjustmentType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="wiederkehrend">Wiederkehrend (z.B. monatliches Gehalt)</SelectItem>
              <SelectItem value="einmalig">Einmalig (nur in einem Monat)</SelectItem>
            </SelectContent>
          </SelectRoot>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Start-Jahr" type="number" value={startYear} onChange={(e) => setStartYear(Number(e.target.value))} />
          <Input label="Start-Monat" type="number" min={1} max={12} value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} />
          <Input
            label="Betrag/Monat (€)"
            type="number" step="100"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            hint="Bei Aufwendungen: positive Zahl"
          />
        </div>
        {type === 'wiederkehrend' && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="End-Jahr (optional)"
              type="number"
              value={endYear ?? ''}
              onChange={(e) => setEndYear(e.target.value ? Number(e.target.value) : null)}
            />
            <Input
              label="End-Monat (optional)"
              type="number" min={1} max={12}
              value={endMonth ?? ''}
              onChange={(e) => setEndMonth(e.target.value ? Number(e.target.value) : null)}
            />
          </div>
        )}
        {error && <p className="text-small text-red-600">{error}</p>}
      </div>
    </Modal>
  )
}
